import { useEffect, useMemo, useRef, useState } from "react";
import { fetchOnboardActive, deleteOnboardActive } from "../utils/api.js";
import SearchInput from "../components/shared/SearchInput";
import HelpButton from "../components/shared/HelpButton";
import OnboardingEditForm from "../components/OnboardingEditForm";
import { rowToRecord, mobileDigits } from "../utils/onboardingFields.js";
import { loadActiveSiteNames } from "../utils/sitesCache.js";
import { cssUrl } from "../utils/cssVersion";

const onboardingStyles = cssUrl("newuseronboarding.css");

/* ============================================================================
 * OnboardActive — the onboarding register, ActivatedModal-style, inline in
 * the Admin tab (replaces OnboardingUsersCards + its edit-modal host).
 *   · one fetch (fetchOnboardActive) pulls the five main-user views —
 *       Complete Working · Complete Not Working · NU Form Working ·
 *       NU Form Not Working · NU No Form            (NU = Newuser)
 *     backend tags every row with its `category`
 *   · five totalling buttons across the full inner-tab width — nothing lit by
 *     default, one at a time, coloured; nothing selected = no table
 *   · search by name (2-tier suggestion: name + email). The first four views
 *     search "Full Name"; NU No Form searches "First_Name"
 *   · two chip rows narrow further — Type Of Employment (blue) and Position
 *     (rose). One chip per row at a time, so at most two chips are lit;
 *     totals on the view buttons honour the chips + search
 *   · rows are a table, not cards: Full Name · First Name · SNL Type ·
 *     Type Of Employment · Position · Working · FormComplete
 *   · click a row → OnboardingEditForm (same editor, same conversion guard)
 *   · NU No Form rows only: a 🗑 icon next to the view flag → confirm dialog →
 *     DELETE /api/onboard-active/:id (deleteOnboardActive) → green/red toast
 *     → soft refresh so the table reflects the deletion in place
 *   · ↻ refresh keeps the lit button, chips and the search
 * ==========================================================================*/

/* ---------- icons ---------- */
const BadgeUserIcon = ({ size = 12 }) => (      // complete · working
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="16" rx="2" /><circle cx="9" cy="10" r="2.5" /><path d="M5.5 17a3.5 3.5 0 0 1 7 0" /><path d="M14 9h4M14 13h4" />
  </svg>
);
const PauseUserIcon = ({ size = 12 }) => (      // complete · not working
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="9" cy="7" r="4" /><path d="M2 21v-2a4 4 0 0 1 4-4h5" /><path d="M16 15v6M20 15v6" />
  </svg>
);
const FileCheckIcon = ({ size = 12 }) => (      // NU form · working
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><path d="M9 15l2 2 4-4" />
  </svg>
);
const FileClockIcon = ({ size = 12 }) => (      // NU form · not working
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h6" /><path d="M14 2v6h6" /><circle cx="17" cy="17" r="4" /><path d="M17 15v2l1.5 1" />
  </svg>
);
const FileOffIcon = ({ size = 12 }) => (        // NU · no form
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><path d="M9.5 12.5l5 5M14.5 12.5l-5 5" />
  </svg>
);
const TrashIcon = ({ size = 12 }) => (          // delete (NU No Form rows only)
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 6h18" /><path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" />
  </svg>
);

/* the five view buttons — `key` must match the backend `category` tag;
 * `nameField` is what the search box matches and what the suggestion shows */
export const OB_FILTERS = [
  { key: "completeWorking",    label: "Complete Working",     color: "#4ade80", Icon: BadgeUserIcon, nameField: "fullName",  title: "SNL_Type is not Newuser · Working" },
  { key: "completeNotWorking", label: "Complete Not Working", color: "#fbbf24", Icon: PauseUserIcon, nameField: "fullName",  title: "SNL_Type is not Newuser · Not Working" },
  { key: "nyFormWorking",      label: "NU Form Working",      color: "#22d3ee", Icon: FileCheckIcon, nameField: "fullName",  title: "Newuser linked to the main database (Master-Link) · Working" },
  { key: "nyFormNotWorking",   label: "NU Form Not Working",  color: "#a78bfa", Icon: FileClockIcon, nameField: "fullName",  title: "Newuser linked to the main database (Master-Link) · Not Working" },
  { key: "nyNoForm",           label: "NU No Form",           color: "#f87171", Icon: FileOffIcon,   nameField: "firstName", title: "Newuser with no Master-Link yet — searched by First Name" },
];
const FILTER_BY_KEY = Object.fromEntries(OB_FILTERS.map((f) => [f.key, f]));
const nameOf = (r) => (r[FILTER_BY_KEY[r.category]?.nameField ?? "fullName"] ?? "").toString();

/* the only view whose rows may be deleted */
const DELETABLE_KEY = "nyNoForm";
const DELETE_COLOR = "#f87171";

/* FormComplete pill — same colours as the cards' status pills */
const FORM_PILL = {
  completed: { color: "#4ade80" },
  new:       { color: "#22d3ee" },
};

/* chip rows — values must match the strings stored in Airtable (matched
 * case-insensitively). One chip per row at a time. */
const CHIP_ROWS = [
  { key: "employment", field: "typeOfEmployment", label: "Type Of Employment", color: "#60a5fa",
    values: ["Sole Trader", "Company", "Partnership", "Employee"] },
  { key: "position",   field: "position",         label: "Position",           color: "#f472b6",
    values: ["Full Time", "Part Time", "Casual"] },
];

const S = {
  chipRow: { display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginTop: 10 },
  chip: (on, color) => ({ padding: "4px 12px", borderRadius: 999, fontSize: "0.72rem", fontWeight: 700, cursor: "pointer",
    border: `1px solid ${on ? color : "rgba(255,255,255,0.15)"}`,
    background: on ? `${color}33` : "rgba(255,255,255,0.03)",
    color: on ? color : "rgba(255,255,255,0.55)", transition: "all .12s ease" }),
  panel: { position: "relative", overflow: "hidden", width: "100%", maxWidth: "100%", boxSizing: "border-box" },
  accent: { position: "absolute", top: 0, left: 0, right: 0, height: 3, background: "linear-gradient(90deg,#22d3ee,#3b82f6,#8b5cf6)" },
  head: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, gap: 10 },
  title: { margin: 0, fontSize: "1rem", fontWeight: 700, color: "#f1f5f9" },
  navBtn: { width: 32, height: 32, borderRadius: 9, border: "1px solid rgba(34,211,238,0.4)",
    background: "rgba(34,211,238,0.1)", color: "#22d3ee", cursor: "pointer", fontSize: "0.95rem", lineHeight: 1,
    display: "inline-flex", alignItems: "center", justifyContent: "center" },
  countPill: { padding: "2px 10px", background: "rgba(34,211,238,0.12)", border: "1px solid rgba(34,211,238,0.3)",
    borderRadius: 999, fontSize: "0.75rem", color: "#7dd3fc", fontWeight: 600, whiteSpace: "nowrap" },
  eyebrow: { fontSize: "0.62rem", letterSpacing: "0.14em", textTransform: "uppercase", color: "#22d3ee", fontWeight: 700 },
  buttonsGrid: { display: "grid", gridTemplateColumns: "repeat(5,minmax(0,1fr))", gap: 10, margin: "12px 0 6px", width: "100%" },
  countBtn: (active, color) => ({ borderRadius: 12, padding: "11px 8px", cursor: "pointer",
    background: active ? "rgba(34,211,238,0.15)" : "rgba(34,211,238,0.08)",
    border: `1px solid ${active ? color : "rgba(34,211,238,0.25)"}`,
    display: "flex", flexDirection: "column", alignItems: "center", gap: 4, minWidth: 0 }),
  countLabel: { display: "inline-flex", alignItems: "center", gap: 5, fontSize: "0.62rem", color: "rgba(255,255,255,0.55)",
    textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 700, textAlign: "center", lineHeight: 1.3 },
  countValue: (color) => ({ fontSize: "1.55rem", fontWeight: 800, lineHeight: 1, color }),
  card: { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: "12px", marginTop: 8 },
  colHead: { fontSize: "0.62rem", letterSpacing: "0.05em", textTransform: "uppercase",
    color: "rgba(255,255,255,0.45)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  cell: { fontSize: "0.82rem", color: "#e2e8f0", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  name: { fontSize: "0.84rem", fontWeight: 700, color: "#22d3ee", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  pill: (color) => ({ display: "inline-block", padding: "2px 9px", borderRadius: 999, fontSize: "0.7rem", fontWeight: 700,
    color, background: `${color}1f`, border: `1px solid ${color}66`, whiteSpace: "nowrap" }),
  flag: (color) => ({ width: 24, height: 24, borderRadius: 7, display: "inline-flex", alignItems: "center", justifyContent: "center",
    color, background: `${color}1f`, border: `1px solid ${color}66`, flexShrink: 0 }),
  trashBtn: (color) => ({ width: 24, height: 24, borderRadius: 7, display: "inline-flex", alignItems: "center", justifyContent: "center",
    color, background: `${color}1f`, border: `1px solid ${color}66`, flexShrink: 0, cursor: "pointer", padding: 0 }),
  empty: { padding: "24px", textAlign: "center", color: "rgba(255,255,255,0.5)", fontStyle: "italic", fontSize: "0.85rem" },
  hint: { marginTop: 10, fontSize: "0.72rem", color: "rgba(255,255,255,0.4)", textAlign: "center" },
  suggestions: { position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "#fff",
    border: "1px solid #e5e7eb", borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.08)", listStyle: "none",
    margin: 0, padding: "4px 0", maxHeight: 240, overflowY: "auto", zIndex: 10 },
  suggestion: { padding: "10px 14px", cursor: "pointer", fontSize: 14, color: "#111827" },
  suggestionSub: { fontSize: 12, color: "#6b7280", marginTop: 2 },
  linkBtn: { background: "none", border: "none", color: "#22d3ee", fontSize: "0.8rem", cursor: "pointer", padding: 0, fontWeight: 600 },
  /* delete confirmation */
  backdrop: { position: "fixed", inset: 0, top: "var(--fr-modal-top, 0px)", background: "rgba(0,0,0,0.7)", zIndex: 998 },
  confirm: { position: "fixed", top: "calc(var(--fr-modal-top, 0px) + (100dvh - var(--fr-modal-top, 0px) - 68px) / 2)", left: "50%", transform: "translate(-50%,-50%)", width: "min(440px, 92vw)", zIndex: 999,
    background: "#1b2537", border: `1px solid ${DELETE_COLOR}66`, borderRadius: 14, padding: "20px 22px", boxSizing: "border-box",
    boxShadow: "0 20px 50px rgba(0,0,0,0.5)" },
  confirmTitle: { margin: 0, fontSize: "1rem", fontWeight: 700, color: "#f1f5f9", display: "flex", alignItems: "center", gap: 8 },
  confirmBody: { margin: "12px 0 18px", fontSize: "0.85rem", color: "rgba(255,255,255,0.7)", lineHeight: 1.5 },
  confirmName: { color: "#22d3ee", fontWeight: 700 },
  confirmBtnRow: { display: "flex", gap: 10, justifyContent: "flex-end" },
  cancelBtn: { padding: "8px 16px", borderRadius: 9, border: "1px solid rgba(255,255,255,0.15)", background: "rgba(255,255,255,0.04)",
    color: "#e2e8f0", cursor: "pointer", fontSize: "0.82rem", fontWeight: 600 },
  deleteBtn: (busy) => ({ padding: "8px 16px", borderRadius: 9, border: `1px solid ${DELETE_COLOR}`, background: `${DELETE_COLOR}33`,
    color: DELETE_COLOR, cursor: busy ? "not-allowed" : "pointer", fontSize: "0.82rem", fontWeight: 700, opacity: busy ? 0.7 : 1,
    display: "inline-flex", alignItems: "center", gap: 6, minWidth: 96, justifyContent: "center" }),
};

const Spin = () => (
  <span style={{ width: 12, height: 12, border: "2px solid currentColor", borderTopColor: "transparent",
    borderRadius: "50%", display: "inline-block", animation: "oba-spin 0.7s linear infinite" }} />
);

/* Full Name | First Name | SNL Type | Type Of Employment | Position | Working | FormComplete | flag (+ 🗑 on NU No Form) */
const TABLE_COLS = "1.4fr 1fr 0.9fr 1.2fr 0.9fr 0.9fr 1fr 80px";
const TABLE_HEADS = ["Full Name", "First Name", "SNL Type", "Type Of Employment", "Position", "Working", "Form Complete", ""];

/* ============================================================================ */
const OnboardActive = () => {
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [query, setQuery] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filter, setFilter] = useState(null);          // one button at a time, none by default
  const [chips, setChips] = useState({ employment: null, position: null }); // one per row
  const [editRecord, setEditRecord] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // row awaiting delete confirmation
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState(null);
  const searchWrapperRef = useRef(null);

  /* ---------- stylesheet (ob-* classes used by the editor + toast) ---------- */
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = onboardingStyles;
    document.head.appendChild(link);
    return () => { document.head.removeChild(link); };
  }, []);

  /* ---------- data ---------- */
  const load = async () => {
    const res = await fetchOnboardActive();
    if (res.status !== "success") throw new Error(res.message || res.error || "Failed to load onboarding users");
    const seen = new Set();
    const out = [];
    (res.data ?? []).forEach((d) => {
      const row = d.row ?? [];
      const rec = {
        ...rowToRecord(row),
        working: row[24] ?? "",
        masterLink: row[25] ?? "",
        category: d.category,
      };
      if (!rec.recordId || seen.has(rec.recordId)) return;   // a record can only sit in one view
      seen.add(rec.recordId);
      out.push(rec);
    });
    return out;
  };

  useEffect(() => {
    let alive = true;
    loadActiveSiteNames();                       // warm the Site Working list for the editor
    (async () => {
      try { const rows = await load(); if (alive) { setAll(rows); setLoadError(null); } }
      catch (err) { if (alive) setLoadError(err.message); }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, []);

  /* soft refresh — refetch in place, keep the lit button, chips + search */
  const refresh = async () => {
    if (refreshing || loading) return;
    setRefreshing(true);
    loadActiveSiteNames(true);                   // refetch sites too
    try { setAll(await load()); setLoadError(null); } catch { /* keep current rows */ }
    setRefreshing(false);
  };

  /* ---------- close suggestions on outside click ---------- */
  useEffect(() => {
    const onDown = (e) => {
      if (searchWrapperRef.current && !searchWrapperRef.current.contains(e.target)) setShowSuggestions(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  /* ---------- toast auto-dismiss ---------- */
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), toast.type === "error" ? 7000 : 3000);
    return () => clearTimeout(t);
  }, [toast]);

  /* ---------- Esc closes the delete confirmation ---------- */
  useEffect(() => {
    if (!confirmDelete) return undefined;
    const onKey = (e) => { if (e.key === "Escape" && !deleting) setConfirmDelete(null); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [confirmDelete, deleting]);

  /* ---------- search scope (totals compute on this) ----------
   * Each row is matched on its own view's name field — Full Name for the
   * first four views, First_Name for NU No Form — plus email. */
  const q = query.trim().toLowerCase();
  const scoped = useMemo(() => {
    let rows = all;
    CHIP_ROWS.forEach((row) => {
      const on = chips[row.key];
      if (!on) return;
      const want = on.toLowerCase();
      rows = rows.filter((r) => (r[row.field] ?? "").toString().trim().toLowerCase() === want);
    });
    if (!q) return rows;
    return rows.filter((r) =>
      nameOf(r).toLowerCase().includes(q) || (r.email ?? "").toString().toLowerCase().includes(q)
    );
  }, [all, chips, q]);
  const anyChip = Object.values(chips).some(Boolean);

  const counts = useMemo(() => {
    const c = {};
    OB_FILTERS.forEach((f) => { c[f.key] = scoped.filter((r) => r.category === f.key).length; });
    return c;
  }, [scoped]);

  const activeDef = filter ? FILTER_BY_KEY[filter] : null;
  const canDelete = activeDef?.key === DELETABLE_KEY;

  /* table rows — the lit button only, sorted by its name field */
  const visible = useMemo(() => {
    if (!activeDef) return [];
    return scoped
      .filter((r) => r.category === activeDef.key)
      .sort((a, b) => nameOf(a).localeCompare(nameOf(b)));
  }, [scoped, activeDef]);

  /* 2-tier suggestions: name (per view) on top, email underneath.
   * Sourced from the lit view when there is one, otherwise everything. */
  const suggestions = useMemo(() => {
    if (!q) return [];
    const src = activeDef ? scoped.filter((r) => r.category === activeDef.key) : scoped;
    const seen = new Set();
    const out = [];
    for (const r of src) {
      const display = nameOf(r);
      if (!display || !display.toLowerCase().includes(q)) continue;
      const k = display.toLowerCase();
      if (seen.has(k)) continue;
      seen.add(k);
      out.push({ display, sub: (r.email ?? "").toString() });
      if (out.length >= 8) break;
    }
    return out;
  }, [q, scoped, activeDef]);

  const toggleChip = (rowKey, value) =>
    setChips((p) => ({ ...p, [rowKey]: p[rowKey] === value ? null : value }));

  const handleSearchChange = (val) => { setQuery(val); setShowSuggestions(true); };
  const handleSelectSuggestion = (s) => { setQuery(s.display); setShowSuggestions(false); };
  const toggleFilter = (key) => setFilter((p) => (p === key ? null : key));

  /* ---------- editor: siblings + save ----------
   * The conversion guard in OnboardingEditForm needs the same person's other
   * records — gather by email, falling back to mobile. */
  const editSiblings = useMemo(() => {
    if (!editRecord) return [];
    const email = (editRecord.email ?? "").toLowerCase();
    if (email) {
      const same = all.filter((r) => (r.email ?? "").toLowerCase() === email);
      if (same.length) return same;
    }
    const digits = mobileDigits(editRecord.mobile);
    if (digits) return all.filter((r) => mobileDigits(r.mobile) === digits);
    return [editRecord];
  }, [editRecord, all]);

  const handleSaved = (payload) => {
    setAll((prev) => prev.map((r) => (r.recordId === payload.recordId ? { ...r, ...payload } : r)));
    setToast({ type: "success", message: "Record updated successfully." });
  };

  /* ---------- delete (NU No Form only) ----------
   * Icon click → confirmDelete holds the row → Delete button calls the API.
   * Success: drop the row locally straight away, green toast, then a soft
   * refresh so the counts/table are re-pulled from Airtable. Failure: red
   * toast, row stays. */
  const askDelete = (e, r) => {
    e.stopPropagation();                         // don't open the editor
    if (!canDelete || r.category !== DELETABLE_KEY) return;
    setConfirmDelete(r);
  };

  const handleDelete = async () => {
    if (!confirmDelete || deleting) return;
    const target = confirmDelete;
    setDeleting(true);
    try {
      const res = await deleteOnboardActive(target.recordId);
      if (res?.status === "success") {
        setAll((prev) => prev.filter((r) => r.recordId !== target.recordId));
        setConfirmDelete(null);
        setToast({ type: "success", message: `${nameOf(target) || "Record"} deleted.` });
        refresh();                               // light re-pull, keeps button / chips / search
      } else {
        throw new Error(res?.message || res?.error || "Failed to delete record");
      }
    } catch (err) {
      setConfirmDelete(null);
      setToast({ type: "error", message: err.message || "Failed to delete record" });
    } finally {
      setDeleting(false);
    }
  };

  const formPill = (v) => {
    const s = (v ?? "").toString();
    const p = FORM_PILL[s.toLowerCase()];
    return p ? <span style={S.pill(p.color)}>{s}</span> : <span style={S.cell}>{s || "—"}</span>;
  };

  /* ---------- render ---------- */
  return (
    <div className="ob-container" style={{ width: "100%", maxWidth: "100%" }}>
      <style>{`@keyframes oba-spin{to{transform:rotate(360deg)}}
        .oba-row{transition:background .12s ease}.oba-row:hover{background:rgba(34,211,238,0.06)}
        .oba-row:focus-visible{outline:2px solid #22d3ee;outline-offset:-2px}
        .oba-trash{transition:background .12s ease,transform .12s ease}
        .oba-trash:hover{background:${DELETE_COLOR}40;transform:scale(1.08)}
        .oba-trash:focus-visible{outline:2px solid ${DELETE_COLOR};outline-offset:1px}`}</style>

      {toast && (
        <div className={`ob-toast ${toast.type === "success" ? "ob-toast-success" : "ob-toast-error"}`}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {toast.type === "success"
              ? <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              : <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />}
          </svg>
          {toast.message}
        </div>
      )}

      {editRecord && (
        <OnboardingEditForm
          record={editRecord}
          userRecords={editSiblings}
          onClose={() => setEditRecord(null)}
          onSaved={handleSaved}
        />
      )}

      {/* delete confirmation */}
      {confirmDelete && (
        <>
          <div style={S.backdrop} onClick={deleting ? undefined : () => setConfirmDelete(null)} />
          <div role="dialog" aria-modal="true" aria-labelledby="oba-del-title" style={S.confirm}>
            <h4 id="oba-del-title" style={S.confirmTitle}>
              <span style={S.flag(DELETE_COLOR)}><TrashIcon /></span>Delete this record?
            </h4>
            <p style={S.confirmBody}>
              This permanently removes <span style={S.confirmName}>{nameOf(confirmDelete) || "this record"}</span>
              {confirmDelete.email ? <> ({confirmDelete.email})</> : null} from the NU No Form list in Airtable.
              This can't be undone.
            </p>
            <div style={S.confirmBtnRow}>
              <button type="button" style={S.cancelBtn} disabled={deleting} onClick={() => setConfirmDelete(null)}>
                Cancel
              </button>
              <button type="button" style={S.deleteBtn(deleting)} disabled={deleting} onClick={handleDelete} autoFocus>
                {deleting ? <><Spin /> Deleting…</> : "Delete"}
              </button>
            </div>
          </div>
        </>
      )}

      <div className="ob-form-card" style={S.panel}>
        <div style={S.accent} />

        {/* header */}
        <div style={S.head}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <h4 style={S.title}>Onboarding</h4>
            <HelpButton helpData={[{ chip: "onboardactive", urlFilter: "/static/pdffilters/OnboardActive.pdf" }]} />
            <span style={S.countPill}>
              {activeDef ? `${visible.length} of ${counts[activeDef.key] ?? 0}` : `${scoped.length} of ${all.length}`}
            </span>
          </div>
          <button type="button" style={{ ...S.navBtn, opacity: refreshing ? 0.6 : 1 }}
            title="Refresh data — keeps the lit button and search" onClick={refresh}>
            {refreshing ? <Spin /> : "↻"}
          </button>
        </div>

        {/* search — 2-tier suggestions */}
        <div ref={searchWrapperRef} style={{ position: "relative" }}>
          <SearchInput
            value={query}
            onChange={handleSearchChange}
            placeholder={activeDef?.nameField === "firstName" ? "Search by first name…" : "Search by full name…"}
          />
          {showSuggestions && suggestions.length > 0 && (
            <ul style={S.suggestions}>
              {suggestions.map((s) => (
                <li key={s.display} style={S.suggestion} onClick={() => handleSelectSuggestion(s)}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#f3f4f6")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                  {s.display}
                  {s.sub && <div style={S.suggestionSub}>{s.sub}</div>}
                </li>
              ))}
            </ul>
          )}
        </div>
        {/* chips — one employment chip (blue) + one position chip (rose) at a time, one line */}
        <div style={S.chipRow}>
          {CHIP_ROWS.map((row, ri) => (
            <div key={row.key} style={{ display: "flex", gap: 6, flexWrap: "wrap", marginLeft: ri ? 10 : 0 }}>
              {row.values.map((v) => {
                const on = chips[row.key] === v;
                return (
                  <button key={v} type="button" style={S.chip(on, row.color)} aria-pressed={on}
                    title={row.label} onClick={() => toggleChip(row.key, v)}>
                    {v}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        {(q || anyChip) && (
          <div style={{ marginTop: 8, display: "flex", gap: 16, flexWrap: "wrap" }}>
            {q && (
              <button type="button" style={S.linkBtn} onClick={() => { setQuery(""); setShowSuggestions(false); }}>
                Clear search
              </button>
            )}
            {anyChip && (
              <button type="button" style={S.linkBtn} onClick={() => setChips({ employment: null, position: null })}>
                Clear chips
              </button>
            )}
          </div>
        )}

        {/* five view buttons — full inner-tab width, one at a time, none lit by default */}
        <div style={S.buttonsGrid}>
          {OB_FILTERS.map((f) => {
            const active = filter === f.key;
            return (
              <button key={f.key} type="button" style={S.countBtn(active, f.color)} title={f.title}
                aria-pressed={active} onClick={() => toggleFilter(f.key)}>
                <span style={S.countLabel}>
                  <span style={{ color: f.color, display: "inline-flex" }}><f.Icon size={12} /></span>{f.label}
                </span>
                <span style={S.countValue(f.color)}>{loading ? "…" : counts[f.key]}</span>
              </button>
            );
          })}
        </div>

        {/* rows table */}
        <div style={S.card}>
          {loading ? (
            <div style={S.empty}>Loading onboarding users…</div>
          ) : loadError ? (
            <div style={{ ...S.empty, color: "#fca5a5" }}>{loadError}</div>
          ) : !activeDef ? (
            <div style={S.empty}>Click a button above to list its records.</div>
          ) : visible.length === 0 ? (
            <div style={S.empty}>No records for this view.</div>
          ) : (
            <>
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 8 }}>
                <span style={{ ...S.eyebrow, color: activeDef.color }}>{activeDef.label}</span>
                <span style={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.45)" }}>{visible.length} records</span>
              </div>
              <div style={{ maxHeight: 480, overflowY: "auto", overflowX: "auto" }}>
                <div style={{ minWidth: 880 }}>
                  <div style={{ display: "grid", gridTemplateColumns: TABLE_COLS, alignItems: "center", padding: "4px 8px 8px",
                    columnGap: 8, borderBottom: "1px solid rgba(255,255,255,0.12)", position: "sticky", top: 0, zIndex: 1, background: "#1b2537" }}>
                    {TABLE_HEADS.map((h, i) => <span key={i} style={S.colHead}>{h}</span>)}
                  </div>
                  {visible.map((r, i) => (
                    <div key={r.recordId} className="oba-row" role="button" tabIndex={0}
                      title="Click to edit this record"
                      onClick={() => setEditRecord({ ...r })}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setEditRecord({ ...r }); } }}
                      style={{ display: "grid", gridTemplateColumns: TABLE_COLS, alignItems: "center", cursor: "pointer",
                        padding: "8px 8px", columnGap: 8, borderTop: i ? "1px solid rgba(255,255,255,0.06)" : "none" }}>
                      <span style={S.name} title={r.fullName}>{r.fullName || "—"}</span>
                      <span style={S.cell} title={r.firstName}>{r.firstName || "—"}</span>
                      <span style={S.cell}>{r.snlType || "—"}</span>
                      <span style={S.cell} title={r.typeOfEmployment}>{r.typeOfEmployment || "—"}</span>
                      <span style={S.cell}>{r.position || "—"}</span>
                      <span style={S.cell}>{r.working || "—"}</span>
                      <span>{formPill(r.formComplete)}</span>
                      <span style={{ display: "flex", justifyContent: "flex-end", gap: 14 }}>
                        <span style={S.flag(activeDef.color)} title={activeDef.label}><activeDef.Icon /></span>
                        {canDelete && (
                          <button type="button" className="oba-trash" style={S.trashBtn(DELETE_COLOR)}
                            title="Delete this record" aria-label={`Delete ${nameOf(r)}`}
                            onClick={(e) => askDelete(e, r)}
                            onKeyDown={(e) => e.stopPropagation()}>
                            <TrashIcon />
                          </button>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <div style={S.hint}>
                {canDelete ? "Click a row to open the record editor · 🗑 deletes the record." : "Click a row to open the record editor."}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnboardActive;